import React from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { stripePaymentService, PaymentData } from '@/services/stripePaymentService';
import { BillingAddress } from './StripePaymentForm';

interface PaymentHandlerProps {
  formData: {
    companyPlan: string;
    selectedFrameworks: string[];
    email: string;
    yourName: string;
    saveCard: boolean;
    enterpriseChoice?: string;
    billingAddress: BillingAddress;
    taxId: string;
  };
  isProcessingPayment: boolean;
  onPaymentStart: () => void;
  onPaymentSuccess: (paymentIntentId?: string) => void;
  onPaymentError: (error: string) => void;
  isEnterprisePlan: boolean;
}

export default function PaymentHandler({
  formData,
  isProcessingPayment,
  onPaymentStart,
  onPaymentSuccess,
  onPaymentError,
  isEnterprisePlan
}: PaymentHandlerProps) {
  const stripe = useStripe();
  const elements = useElements();

  const handlePayment = async () => {
    if (!stripe || !elements) {
      onPaymentError('Stripe not initialized');
      return;
    }

    onPaymentStart();

    try {
      const paymentData: PaymentData = {
        companyPlan: formData.companyPlan,
        selectedFrameworks: formData.selectedFrameworks,
        customerEmail: formData.email,
        customerName: formData.yourName,
        saveCard: formData.saveCard,
        enterpriseChoice: formData.enterpriseChoice,
        billingAddress: formData.billingAddress,
        taxId: formData.taxId,
      };

      const result = await stripePaymentService.processPayment(paymentData, elements, stripe);

      if (result.success) {
        console.log('Payment processed successfully:', result.paymentIntentId);
        onPaymentSuccess(result.paymentIntentId);
      } else {
        onPaymentError(result.error || 'Payment processing failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      onPaymentError('Payment processing failed. Please try again.');
    }
  };

  // Don't render button if it's enterprise plan with meeting choice
  if (isEnterprisePlan && formData.enterpriseChoice === 'meeting') {
    return null;
  }

  // Don't render if enterprise plan but no choice made
  if (isEnterprisePlan && !formData.enterpriseChoice) {
    return null;
  }

  return (
    <Button
      onClick={handlePayment}
      disabled={isProcessingPayment || !stripe || !elements}
      className="w-full"
      size="lg"
    >
      {isProcessingPayment ? (
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          Processing Payment...
        </div>
      ) : (
        `Complete Payment${isEnterprisePlan ? ' ($3,500/month)' : ''}`
      )}
    </Button>
  );
}
